import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './DocumentList.sass'
import InfiniteScrollLoader from 'components/InfiniteScrollLoader'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import DocumentListItem from '../DocumentListItem'
import { Id } from 'types/definitions'
import { EmptyState, EmptyStateIllustrationImage } from 'components/EmptyState'
import JobDocumentsViewModel from '../../../jobs/views/JobRoot/Job/JobDocumentsViewModel'
import { CurrentMemberContext } from 'contexts/CurrentMemberContext'
import { useBrandingSettings } from 'hooks/branding'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  documentsVM: JobDocumentsViewModel
  selectable?: boolean
  compact?: boolean
  showIcon?: boolean
  isCardLayout?: boolean
}

export default observer(function DocumentList({
  documentsVM,
  selectable,
  compact,
  showIcon,
}: Props) {
  const currentMember = React.useContext(CurrentMemberContext)
  const canUploadDocs = currentMember.hasPermission('DOCS_UPLOAD')
  const { corporateMode } = useBrandingSettings()

  return (
    <div className={cx(styles.root)}>
      {documentsVM.activate.match({
        pending: () => <ContentLoading />,
        rejected: (err) => <ContentError error={err} />,
        resolved: () => {
          return (
            <div className={styles.documentsList}>
              {documentsVM.filteredDocuments.length > 0 ? (
                <>
                  {documentsVM.filteredDocuments.map((document) => {
                    return (
                      <div key={Id.assert(document.id)}>
                        <DocumentListItem
                          document={document}
                          documentsVM={documentsVM}
                          documentClickAction={() =>
                            documentsVM.viewDocumentDetails(document)
                          }
                          selectable={selectable}
                          showIcon={showIcon}
                        />
                      </div>
                    )
                  })}
                  <InfiniteScrollLoader
                    scrollProvider={documentsVM.scrollProvider!}
                  />
                </>
              ) : (
                <div
                  className={cx(
                    styles.emptyStateContainer,
                    !compact && styles.emptyPadding
                  )}
                >
                  <EmptyState
                    hideIllustration={corporateMode}
                    title={
                      documentsVM.filterTags.length === 0
                        ? canUploadDocs
                          ? 'Upload your first document'
                          : 'No documents'
                        : 'No matching documents.'
                    }
                    image={
                      <EmptyStateIllustrationImage
                        src={
                          documentsVM.filterTags.length === 0
                            ? require('assets/images/img_upload-docs.svg')
                            : require('assets/images/img_search-noresults.svg')
                        }
                      />
                    }
                    subtitle={
                      documentsVM.filterTags.length === 0 ? (
                        canUploadDocs ? (
                          'There are no documents in this workspace. Lets go create some!'
                        ) : (
                          'No documents have been uploaded to this job'
                        )
                      ) : (
                        <>
                          There are no documents with the{' '}
                          <strong>{documentsVM.filterTags[0].label}</strong>{' '}
                          tag.
                        </>
                      )
                    }
                    small
                  />
                </div>
              )}
            </div>
          )
        },
      })}
    </div>
  )
})
